import React, { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Chart, Bar, Line, Doughnut } from "react-chartjs-2";
import "./DashboardPage.css";
import "./Statistics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CHART_COLORS = {
  primary: "rgba(74, 144, 217, 0.9)",
  primaryLight: "rgba(74, 144, 217, 0.5)",
  secondary: "rgba(56, 161, 105, 0.9)",
  tertiary: "rgba(237, 137, 54, 0.9)",
  quaternary: "rgba(229, 62, 62, 0.9)",
  grid: "rgba(0, 0, 0, 0.06)",
};

// Mock: Günlere göre müşteri sayısı
const dailyTrafficData = {
  labels: ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"],
  datasets: [
    {
      label: "Müşteri Sayısı",
      data: [124, 98, 156, 142, 178, 89, 67],
      backgroundColor: CHART_COLORS.primaryLight,
      borderColor: CHART_COLORS.primary,
      borderWidth: 1,
    },
  ],
};

// Mock: Saatlere göre yoğunluk (09-18 arası)
const hourlyTrafficData = {
  labels: ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"],
  datasets: [
    {
      label: "Yoğunluk",
      data: [12, 28, 45, 62, 38, 52, 48, 35, 22, 15],
      borderColor: CHART_COLORS.secondary,
      backgroundColor: "rgba(56, 161, 105, 0.2)",
      fill: true,
      tension: 0.3,
    },
  ],
};

// Mock: Bekleme süreleri dağılımı (dakika aralıkları)
const waitingTimeData = {
  labels: ["0-5 dk", "5-10 dk", "10-15 dk", "15-20 dk", "20-30 dk", "30+ dk"],
  datasets: [
    {
      label: "Müşteri Sayısı",
      data: [85, 120, 95, 45, 28, 12],
      backgroundColor: [
        CHART_COLORS.secondary,
        CHART_COLORS.primary,
        "rgba(74, 144, 217, 0.7)",
        CHART_COLORS.tertiary,
        "rgba(237, 137, 54, 0.7)",
        CHART_COLORS.quaternary,
      ],
      borderWidth: 0,
    },
  ],
};

// Mock: Aylara göre memnuniyet eğrisi (1-10 puan)
const satisfactionTrendData = {
  labels: ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"],
  datasets: [
    {
      label: "Ortalama Memnuniyet (1-10)",
      data: [6.2, 6.5, 6.8, 7.0, 7.2, 7.4, 7.1, 7.5, 7.8, 8.0, 7.9, 8.2],
      borderColor: CHART_COLORS.primary,
      backgroundColor: "rgba(74, 144, 217, 0.15)",
      fill: true,
      tension: 0.4,
    },
  ],
};

// Mock: Memnuniyet anketi kategorileri
const satisfactionSurveyData = {
  labels: ["Çok Memnun", "Memnun", "Kararsız", "Memnun Değil", "Hiç Memnun Değil"],
  datasets: [
    {
      data: [42, 35, 12, 7, 4],
      backgroundColor: [
        "rgba(56, 161, 105, 0.9)",
        "rgba(74, 144, 217, 0.9)",
        "rgba(156, 163, 175, 0.9)",
        "rgba(237, 137, 54, 0.9)",
        "rgba(229, 62, 62, 0.9)",
      ],
      borderWidth: 0,
    },
  ],
};

// Mock: Haftalık toplam müşteri trendi (son 8 hafta)
const weeklyTrendData = {
  labels: ["Hafta 1", "Hafta 2", "Hafta 3", "Hafta 4", "Hafta 5", "Hafta 6", "Hafta 7", "Hafta 8"],
  datasets: [
    {
      label: "Toplam Müşteri",
      data: [620, 658, 712, 689, 745, 780, 762, 801],
      borderColor: CHART_COLORS.primary,
      backgroundColor: "rgba(74, 144, 217, 0.1)",
      fill: true,
      tension: 0.3,
    },
  ],
};

const defaultOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      position: "top",
    },
  },
  scales: {
    x: {
      grid: { color: CHART_COLORS.grid },
    },
    y: {
      grid: { color: CHART_COLORS.grid },
      beginAtZero: true,
    },
  },
};

const TABS = [
  { id: "density", label: "Müşteri Yoğunluğu" },
  { id: "waiting", label: "Bekleme Süreleri" },
  { id: "satisfaction", label: "Memnuniyet" },
  { id: "trend", label: "Haftalık Trend" },
];

export default function Statistics() {
  const [activeTab, setActiveTab] = useState("density");

  return (
    <div className="dashboard-page statistics-page">
      <h2>İstatistikler</h2>

      <div className="statistics-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`statistics-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="statistics-content">
        {activeTab === "density" && (
          <div className="statistics-charts">
            <div className="statistics-chart-card">
              <h3>Günlere Göre Müşteri Sayısı</h3>
              <div className="chart-container chart-bar">
                <Bar
                  data={dailyTrafficData}
                  options={{
                    ...defaultOptions,
                    plugins: {
                      ...defaultOptions.plugins,
                      title: { display: false },
                    },
                  }}
                />
              </div>
            </div>
            <div className="statistics-chart-card">
              <h3>Saatlere Göre Yoğunluk</h3>
              <div className="chart-container chart-line">
                <Line
                  data={hourlyTrafficData}
                  options={{
                    ...defaultOptions,
                    plugins: {
                      ...defaultOptions.plugins,
                      title: { display: false },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "waiting" && (
          <div className="statistics-charts">
            <div className="statistics-chart-card statistics-chart-card-wide">
              <h3>Bekleme Süreleri Dağılımı (Dakika)</h3>
              <div className="chart-container chart-bar">
                <Bar
                  data={waitingTimeData}
                  options={{
                    ...defaultOptions,
                    plugins: {
                      ...defaultOptions.plugins,
                      title: { display: false },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "satisfaction" && (
          <div className="statistics-charts">
            <div className="statistics-chart-card">
              <h3>Memnuniyet Eğrisi (Aylık Ortalama)</h3>
              <div className="chart-container chart-line">
                <Line
                  data={satisfactionTrendData}
                  options={{
                    ...defaultOptions,
                    scales: {
                      ...defaultOptions.scales,
                      y: {
                        ...defaultOptions.scales.y,
                        min: 0,
                        max: 10,
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div className="statistics-chart-card">
              <h3>Anket Sonuçları (Kategoriler)</h3>
              <div className="chart-container chart-doughnut">
                <Doughnut
                  data={satisfactionSurveyData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { position: "bottom" },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "trend" && (
          <div className="statistics-charts">
            <div className="statistics-chart-card statistics-chart-card-wide">
              <h3>Son 8 Hafta Toplam Müşteri</h3>
              <div className="chart-container chart-line">
                <Line
                  data={weeklyTrendData}
                  options={{
                    ...defaultOptions,
                    plugins: {
                      ...defaultOptions.plugins,
                      title: { display: false },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
